import { useSnackbar } from 'notistack';
import { useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from 'react-query';
import { Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material';

import { buildNavItems, useApi, useAuth, useCerebroNav } from '../../shared/context';
import { QueryKeys } from '../../shared/hooks';
import { AxiosUtils } from '../../shared/utils';
import { LoanApplicationTask } from '../../shared/models';
import { CerebroTextButton } from '../../shared/components';
import { CerebroApiEndpoints } from '../../shared/utils/api';

export const MarkDocumentAsNotApplicableDialog: React.FC<MarkDocumentAsNotApplicableDialogProps> = ({
	open = false,
	onClose,
	document: requiredDocument,
}) => {
	const api = useApi();
	const nav = useCerebroNav();
	const client = useQueryClient();
	const { currentUser } = useAuth();
	const { guid } = useParams<'guid'>();

	const snackbar = useSnackbar();
	const queryClient = useQueryClient();

	const mutation = useMutation<LoanApplicationTask, unknown, LoanApplicationTask>(
		document => {
			return AxiosUtils.getResponseData<LoanApplicationTask>(
				api.patch(CerebroApiEndpoints.RequiredDocuments.Specific(guid, document?.guid ?? ''), {
					updateType: 'MarkNa',
					notApplicable: true,
					completed: false,
					uploads: [],
				})
			);
		},
		{
			onSuccess: (_data, document) => {
				queryClient.invalidateQueries(QueryKeys.RequiredDocuments.Root(guid)).finally(() => {
					const navState = buildNavItems(
						currentUser,
						client.getQueryData(QueryKeys.Allocation(guid)),
						client.getQueryData(QueryKeys.Deal.Root(guid)),
						location.pathname,
						client.getQueryData(QueryKeys.Questions.LoanApp),
						client.getQueryData(QueryKeys.Product(guid)),
						client.getQueryData(QueryKeys.RequiredDocuments.Root(guid)),
						client.getQueryData(QueryKeys.Integrations.Config())
					);
					nav.setState(navState);
				});
				snackbar.enqueueSnackbar(`Successfully marked '${document.applicationTaskTitle}' as Not Applicable.`, {
					variant: 'success',
				});
			},
			onError: () => {
				snackbar.enqueueSnackbar('Something went wrong when marking the document as N/A.', {
					variant: 'error',
				});
			},
			onSettled: onClose,
		}
	);

	return (
		<Dialog open={open} onClose={onClose}>
			<DialogTitle>Confirm Action</DialogTitle>
			<DialogContent>
				<DialogContentText>
					Are you sure you want to mark &lsquo;{requiredDocument.applicationTaskTitle}&rsquo; as N/A?
				</DialogContentText>
			</DialogContent>
			<DialogActions>
				<CerebroTextButton onClick={onClose}>No</CerebroTextButton>
				<CerebroTextButton onClick={() => mutation.mutate(requiredDocument)}>Yes</CerebroTextButton>
			</DialogActions>
		</Dialog>
	);
};

interface MarkDocumentAsNotApplicableDialogProps {
	open?: boolean;
	onClose: () => void;
	document: LoanApplicationTask;
}
